import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { TourProvider } from './context/TourContext';
import ErrorBoundary from './components/ErrorBoundary';
import Home from './pages/Home';
import About from './pages/About';
import UseCase from './pages/UseCase';
import Blog from './pages/Blog';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Login from './pages/Login';
import CompanySignupWizard from './pages/CompanySignupWizard';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import UserSettings from './pages/UserSettings';
import Management from './pages/Management';
import PricingManagement from './pages/PricingManagement';
import Margins from './pages/Margins';
import RecordInquiryLog from './pages/RecordInquiryLog';
import VehicleRecord from './pages/VehicleRecord';
import NMVTISRecord from './pages/NMVTISRecord';
import VINplusRecord from './pages/VINplusRecord';
import VINDecode from './pages/VINDecode';
import LienTheftRecord from './pages/LienTheftRecord';
import DrivingRecord from './pages/DrivingRecord';
import MotorVehicleRecord from './pages/MotorVehicleRecord';
import PlateToVINRecord from './pages/PlateToVINRecord';
import Reports from './pages/Reports';
import AcceptInvitation from './pages/AcceptInvitation';
import Notifications from './pages/Notifications';
import NotificationManagement from './pages/NotificationManagement';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ProductVehicleHistory from './pages/ProductVehicleHistory';
import ProductDMVRecords from './pages/ProductDMVRecords';
import ProductVINDecode from './pages/ProductVINDecode';
import ProductTheftLienCheck from './pages/ProductTheftLienCheck';
import ProblemReports from './pages/admin/ProblemReports';
import ProblemReportDetail from './pages/admin/ProblemReportDetail';
import ApplicationSettings from './pages/admin/ApplicationSettings';
import Jurisdictions from './pages/admin/Jurisdictions';
import PortalCredentials from './pages/admin/PortalCredentials';
import JurisdictionEdit from './pages/admin/JurisdictionEdit';
import Clients from './pages/admin/Clients';
import ClientEdit from './pages/admin/ClientEdit';
import ClientApprovals from './pages/admin/ClientApprovals';
import UserActivity from './pages/admin/UserActivity';
import AdminReports from './pages/admin/Reports';
import ReportIssue from './pages/ReportIssue';
import AstsPublicInquiry from './pages/AstsPublicInquiry';
import Invoicing from './pages/Invoicing';
import './globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <ErrorBoundary>
          <TourProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/use-case" element={<UseCase />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/login" element={<Login />} />
            {/* Registration now uses the multi-step onboarding wizard. The legacy
                single-form Register page is kept in the codebase but no longer routed. */}
            <Route path="/register" element={<CompanySignupWizard />} />
            <Route path="/signup" element={<CompanySignupWizard />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<UserSettings />} />
            <Route path="/management" element={<Management />} />
            <Route path="/pricing-management" element={<PricingManagement />} />
            <Route path="/margins" element={<Margins />} />
            {/* '/client-discounts' route retired — superseded by per-client Service Pricing
                + Per-State Pricing on the client editor. */}
            <Route path="/record-inquiry-log" element={<RecordInquiryLog />} />
            <Route path="/vehicle-record" element={<VehicleRecord />} />
            <Route path="/nmvtis-record" element={<NMVTISRecord />} />
            <Route path="/vinplus-record" element={<VINplusRecord />} />
            <Route path="/vin-decode" element={<VINDecode />} />
            <Route path="/lien-theft-record" element={<LienTheftRecord />} />
            <Route path="/driving-record" element={<DrivingRecord />} />
            <Route path="/motor-vehicle-record" element={<MotorVehicleRecord />} />
            <Route path="/asts-public-inquiry" element={<AstsPublicInquiry />} />
            <Route path="/plate-to-vin-record" element={<PlateToVINRecord />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/notification-management" element={<NotificationManagement />} />
            <Route path="/products/vehicle-history" element={<ProductVehicleHistory />} />
            <Route path="/products/dmv-records" element={<ProductDMVRecords />} />
            <Route path="/products/vin-decode" element={<ProductVINDecode />} />
            <Route path="/products/theft-lien-check" element={<ProductTheftLienCheck />} />
            <Route path="/admin/problem-reports" element={<ProblemReports />} />
            <Route path="/admin/problem-reports/:id" element={<ProblemReportDetail />} />
            <Route path="/admin/application-settings" element={<ApplicationSettings />} />
            <Route path="/admin/portal-credentials" element={<PortalCredentials />} />
            <Route path="/admin/jurisdictions" element={<Jurisdictions />} />
            <Route path="/admin/jurisdictions/:code" element={<JurisdictionEdit />} />
            <Route path="/admin/clients" element={<Clients />} />
            <Route path="/admin/clients/:id" element={<ClientEdit />} />
            <Route path="/admin/approvals" element={<ClientApprovals />} />
            <Route path="/admin/user-activity" element={<UserActivity />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/report-issue" element={<ReportIssue />} />
            <Route path="/invoicing" element={<Invoicing />} />
          </Routes>
          </TourProvider>
          </ErrorBoundary>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
